import * as styles from "./HoursField.css";

import { type JSX, type ReactNode, useMemo, useRef, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  TimePicker,
  TimePickerContent,
  TimePickerTrigger,
  TimePickerValue,
} from "@frontend/components/atoms/TimePicker";
import {
  type TimeRange,
  TimeRangePicker,
  TimeRangePickerAxis,
  TimeRangePickerOverview,
  TimeRangePickerTrack,
} from "@frontend/components/atoms/TimeRangePicker";
import { formatMinutes, minutesBetween } from "@frontend/utils/duration";
import { Trans, useLingui } from "@lingui/react/macro";

const DEFAULT_WINDOW = { min: 6 * 60, max: 20 * 60 };

const MARGIN = 60;

const DAY = 24 * 60;

const STEP = 15;

const FIRST_RANGE = { begin: 8 * 60, end: 9 * 60 };

export type HourRow = {
  begin: string | null;
  end: string | null;
};

export type HoursFieldProps<TRow extends HourRow> = {
  rows: TRow[];
  onRowsChange: (rows: TRow[]) => void;
  create: (range: TimeRange) => TRow;
  label?: string;
  hint?: ReactNode;
};

export function HoursField<TRow extends HourRow>({
  rows,
  onRowsChange: setRows,
  create,
  label,
  hint,
}: HoursFieldProps<TRow>): JSX.Element {
  const { t } = useLingui();

  const [selected, setSelected] = useState<number | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);

  const drawn = useMemo(
    () =>
      rows.flatMap((row, index) =>
        row.begin && row.end ? [{ index, begin: row.begin, end: row.end }] : [],
      ),
    [rows],
  );

  const value = useMemo<TimeRange[]>(
    () => drawn.map(({ begin, end }) => ({ begin, end })),
    [drawn],
  );

  const window = useMemo(() => bounds(value), [value]);

  const total = rows.reduce(
    (sum, hour) => sum + (minutesBetween(hour.begin, hour.end) ?? 0),
    0,
  );

  const write = (position: number, range: Partial<HourRow>) =>
    setRows(
      rows.map((row, index) =>
        index === position ? { ...row, ...range } : row,
      ),
    );

  // Moving, resizing and drawing all come back through here
  const apply = (next: TimeRange[]) => {
    const updated = [...rows];

    next.forEach((range, at) => {
      const position = drawn[at]?.index;

      if (undefined === position) {
        updated.push(create(range));

        return;
      }

      updated[position] = {
        ...updated[position]!,
        begin: range.begin,
        end: range.end,
      };
    });

    setRows(updated);
  };

  const remove = (at: number) => {
    const position = drawn[at]?.index;

    if (undefined !== position) {
      setRows(rows.filter((_, index) => index !== position));
    }

    setSelected(null);

    return false;
  };

  const add = () => {
    const last = value.reduce(
      (latest, range) => Math.max(latest, minutesOf(range.end) ?? 0),
      0,
    );

    const range =
      0 === last ? FIRST_RANGE : { begin: last, end: Math.min(DAY, last + 60) };

    apply([...value, { begin: clock(range.begin), end: clock(range.end) }]);
    setSelected(value.length);
  };

  return (
    <div className={styles.hours}>
      <TimeRangePicker
        value={value}
        onValueChange={apply}
        selectedIndex={selected}
        onSelectedIndexChange={setSelected}
        onRemove={remove}
        selectionBoundary={editorRef}
        min={clock(window.min)}
        max={clock(window.max)}
        step={STEP}
        minDuration={STEP}
      >
        <TimeRangePickerTrack aria-label={label ?? t`Hours`} />
        <TimeRangePickerAxis />
        <TimeRangePickerOverview aria-label={t`Visible hours`} />
      </TimeRangePicker>

      <div className={styles.footer} ref={editorRef}>
        <Button
          variant="ghost"
          size="sm"
          leftSection={<Icon name="Plus" size="sm" />}
          onClick={add}
        >
          {t`Add a range`}
        </Button>

        {0 === value.length && hint ? (
          <span className={styles.hint}>{hint}</span>
        ) : null}

        {drawn.map((entry, at) => (
          <div key={entry.index} className={styles.entry}>
            <TimePicker
              range
              layout="text"
              minuteStep={STEP}
              value={[entry.begin, entry.end]}
              onValueChange={([begin, end]) =>
                write(entry.index, { begin, end })
              }
            >
              <TimePickerTrigger onClick={() => setSelected(at)}>
                <TimePickerValue />
              </TimePickerTrigger>

              <TimePickerContent />
            </TimePicker>

            <Button
              variant="ghost"
              size="sm"
              aria-label={t`Remove this range`}
              onClick={() => remove(at)}
            >
              <Icon name="X" size="sm" />
            </Button>
          </div>
        ))}

        {0 < total ? (
          <span className={styles.total}>
            <Trans>Total</Trans>

            <span className={styles.totalValue}>{formatMinutes(total)}</span>
          </span>
        ) : null}
      </div>
    </div>
  );
}

function bounds(ranges: TimeRange[]): { min: number; max: number } {
  let { min, max } = DEFAULT_WINDOW;

  for (const range of ranges) {
    const begin = minutesOf(range.begin);
    const end = minutesOf(range.end);

    if (null !== begin && begin < min) {
      min = Math.max(0, floorHour(begin) - MARGIN);
    }

    if (null !== end && end > max) {
      max = Math.min(DAY, ceilHour(end) + MARGIN);
    }
  }

  return { min, max };
}

function minutesOf(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/u.exec(value);

  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

function clock(minutes: number): string {
  const hours = Math.floor(minutes / 60);

  return `${pad(hours)}:${pad(minutes % 60)}`;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function floorHour(minutes: number): number {
  return Math.floor(minutes / 60) * 60;
}

function ceilHour(minutes: number): number {
  return Math.ceil(minutes / 60) * 60;
}
